import React, { useEffect, useState, useRef } from "react";
import {
  Upload, RefreshCw, Download, FileText, AlertCircle,
  CheckCircle, Clock, Loader2, X, Info,
} from "lucide-react";
import { toast } from "react-toastify";
import {
  listImportJobs, getImportJob, downloadSampleCsv, errorsCsvUrl,
} from "../../apis/contactImports";
import ImportWizard from "../../components/contacts/importer/ImportWizard";

const POLL_MS = 3000;

const STATUS_BADGE = {
  pending:    { color: "bg-gray-100 text-gray-700",     icon: Clock,       label: "Queued" },
  processing: { color: "bg-amber-50 text-amber-700",    icon: Loader2,     label: "Processing" },
  completed:  { color: "bg-emerald-50 text-emerald-700",icon: CheckCircle, label: "Completed" },
  failed:     { color: "bg-rose-50 text-rose-700",      icon: AlertCircle, label: "Failed" },
};

const ImportsPage = ({ phoneId }) => {
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [wizardOpen, setWizardOpen] = useState(false);
  const [errorJob, setErrorJob] = useState(null); // job to display in errors modal
  const pollRef = useRef(null);

  const fetchJobs = async () => {
    if (!phoneId) return;
    try {
      const r = await listImportJobs({ phone_id: phoneId, limit: 30 });
      setJobs(r.data.jobs || []);
    } catch (err) {
      toast.error(err.response?.data?.error || "Failed to load imports");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    setLoading(true);
    fetchJobs();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [phoneId]);

  useEffect(() => {
    const inflight = jobs.some((j) => j.status === "pending" || j.status === "processing");
    clearTimeout(pollRef.current);
    if (inflight) {
      pollRef.current = setTimeout(fetchJobs, POLL_MS);
    }
    return () => clearTimeout(pollRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [jobs]);

  const onDownloadSample = async () => {
    try {
      const r = await downloadSampleCsv();
      const blob = new Blob([r.data], { type: "text/csv" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = "contacts-sample.csv";
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      toast.error(err.response?.data?.error || "Failed to download sample");
    }
  };

  const onPoll = async (id) => {
    try {
      const r = await getImportJob(id);
      setJobs((prev) => prev.map((j) => (j.id === id ? { ...j, ...r.data.job } : j)));
    } catch (_) {}
  };

  const progressPct = (j) => {
    if (j.status === "completed") return 100;
    if (!j.total_rows) return null;
    return Math.min(99, Math.floor(((j.processed_rows || 0) / j.total_rows) * 100));
  };

  return (
    <div className="space-y-4">
      <div className="bg-white rounded-xl border border-gray-200">
        <div className="p-4 border-b border-gray-100 flex items-center justify-between">
          <div>
            <h2 className="text-base font-semibold text-gray-900">Imports</h2>
            <p className="text-xs text-gray-500 mt-0.5">
              Upload CSVs of any size — processing happens in the background in 500-row batches.
            </p>
          </div>
          <div className="flex gap-2">
            <button
              onClick={onDownloadSample}
              className="inline-flex items-center gap-1.5 px-3 py-2 text-sm border border-gray-200 rounded-md hover:bg-gray-50"
            >
              <Download size={14} /> Sample CSV
            </button>
            <button
              onClick={() => setWizardOpen(true)}
              className="inline-flex items-center gap-1.5 px-3 py-2 text-sm bg-emerald-600 text-white rounded-md hover:bg-emerald-700"
            >
              <Upload size={14} /> New import
            </button>
          </div>
        </div>

        <div className="overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead className="bg-gray-50 text-gray-500 text-xs uppercase tracking-wide">
              <tr>
                <th className="px-3 py-2.5 text-left">File</th>
                <th className="px-3 py-2.5 text-left">Status</th>
                <th className="px-3 py-2.5 text-left">Progress</th>
                <th className="px-3 py-2.5 text-left">Inserted / Updated / Failed</th>
                <th className="px-3 py-2.5 text-left">Started</th>
                <th className="px-3 py-2.5 w-32" />
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={6} className="px-3 py-8 text-center text-gray-400">Loading…</td></tr>
              ) : jobs.length === 0 ? (
                <tr><td colSpan={6} className="px-3 py-12 text-center text-gray-400">No imports yet.</td></tr>
              ) : jobs.map((j) => {
                const badge = STATUS_BADGE[j.status] || STATUS_BADGE.pending;
                const Icon = badge.icon;
                const pct = progressPct(j);
                const hasIssues = j.status === "failed" || (j.failed_rows || 0) > 0 || j.error_message;
                return (
                  <React.Fragment key={j.id}>
                    <tr className="border-t border-gray-100">
                      <td className="px-3 py-2.5 align-top">
                        <div className="flex items-center gap-2">
                          <FileText size={14} className="text-gray-400" />
                          <span className="font-medium text-gray-900 truncate max-w-[300px]" title={j.filename}>
                            {j.filename || "(no name)"}
                          </span>
                        </div>
                        <p className="text-[11px] text-gray-400 mt-0.5">
                          {j.total_rows ? `${j.total_rows.toLocaleString()} rows` : "—"}
                        </p>
                      </td>
                      <td className="px-3 py-2.5 align-top">
                        <span className={`inline-flex items-center gap-1 px-2 py-0.5 text-xs rounded-full ${badge.color}`}>
                          <Icon size={12} className={j.status === "processing" ? "animate-spin" : ""} />
                          {badge.label}
                        </span>
                      </td>
                      <td className="px-3 py-2.5 w-48 align-top">
                        {pct === null ? (
                          <span className="text-gray-400">—</span>
                        ) : (
                          <div>
                            <div className="h-1.5 bg-gray-100 rounded-full overflow-hidden">
                              <div className={`h-full ${j.status === "failed" ? "bg-rose-500" : "bg-emerald-500"}`} style={{ width: `${pct}%` }} />
                            </div>
                            <span className="text-[11px] text-gray-500">{pct}% · {(j.processed_rows || 0).toLocaleString()} / {(j.total_rows || 0).toLocaleString()}</span>
                          </div>
                        )}
                      </td>
                      <td className="px-3 py-2.5 text-xs align-top">
                        <span className="text-emerald-700">+{(j.inserted_rows || 0).toLocaleString()}</span>
                        {" "}/{" "}
                        <span className="text-sky-700">{(j.updated_rows || 0).toLocaleString()}</span>
                        {" "}/{" "}
                        <span className="text-rose-700">{(j.failed_rows || 0).toLocaleString()}</span>
                      </td>
                      <td className="px-3 py-2.5 text-xs text-gray-600 align-top">
                        {j.started_at ? new Date(j.started_at).toLocaleString() : "—"}
                      </td>
                      <td className="px-3 py-2.5 text-right whitespace-nowrap align-top">
                        {(j.status === "pending" || j.status === "processing") && (
                          <button onClick={() => onPoll(j.id)} className="p-1.5 rounded text-gray-600 hover:bg-gray-100" title="Refresh">
                            <RefreshCw size={14} />
                          </button>
                        )}
                        {hasIssues && (
                          <button
                            onClick={() => setErrorJob(j)}
                            className="p-1.5 rounded text-rose-600 hover:bg-rose-50"
                            title="View errors"
                          >
                            <Info size={14} />
                          </button>
                        )}
                        {j.failed_rows > 0 && (
                          <a
                            href={errorsCsvUrl(j.id)}
                            target="_blank"
                            rel="noreferrer"
                            className="p-1.5 inline-flex rounded text-gray-600 hover:bg-gray-100"
                            title="Download error report"
                          >
                            <Download size={14} />
                          </a>
                        )}
                      </td>
                    </tr>
                    {/* Inline failure banner directly under the row when the
                        whole job died (vs per-row issues) */}
                    {j.status === "failed" && j.error_message && (
                      <tr>
                        <td colSpan={6} className="px-3 pb-2.5">
                          <div className="bg-rose-50 border border-rose-200 text-rose-800 text-xs px-3 py-2 rounded-md flex items-start gap-2">
                            <AlertCircle size={14} className="mt-0.5 shrink-0" />
                            <div className="min-w-0">
                              <p className="font-medium">Import failed</p>
                              <p className="break-words">{j.error_message}</p>
                            </div>
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {wizardOpen && (
        <ImportWizard
          phoneId={phoneId}
          onClose={() => setWizardOpen(false)}
          onComplete={() => {
            setWizardOpen(false);
            toast.success("Import queued. Will be updated soon.");
            fetchJobs();
          }}
        />
      )}

      {errorJob && (
        <ImportErrorsModal
          jobId={errorJob.id}
          onClose={() => setErrorJob(null)}
        />
      )}
    </div>
  );
};

const ImportErrorsModal = ({ jobId, onClose }) => {
  const [job, setJob] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    getImportJob(jobId)
      .then((r) => { if (alive) setJob(r.data.job); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [jobId]);

  const errors = Array.isArray(job?.error_log) ? job.error_log : [];

  return (
    <>
      <div className="fixed inset-0 bg-black/40 z-40" onClick={onClose} />
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-xl shadow-2xl w-full max-w-2xl max-h-[85vh] flex flex-col">
          <div className="px-5 py-4 border-b border-gray-200 flex items-center justify-between">
            <div className="min-w-0">
              <h3 className="text-base font-semibold text-gray-900 truncate">
                Import errors{job?.filename ? ` · ${job.filename}` : ""}
              </h3>
              <p className="text-xs text-gray-500 mt-0.5">
                Job #{jobId}
                {job ? ` · ${(job.failed_rows || 0).toLocaleString()} failed of ${(job.total_rows || 0).toLocaleString()} rows` : ""}
              </p>
            </div>
            <button onClick={onClose} className="p-1.5 rounded hover:bg-gray-100">
              <X size={18} />
            </button>
          </div>

          <div className="flex-1 overflow-y-auto px-5 py-4 space-y-3">
            {loading ? (
              <p className="text-sm text-gray-400">Loading…</p>
            ) : !job ? (
              <p className="text-sm text-rose-600">Could not load job.</p>
            ) : (
              <>
                {job.error_message && (
                  <div className="bg-rose-50 border border-rose-200 text-rose-800 text-sm px-3 py-2 rounded-md">
                    <p className="font-medium">Import-level failure</p>
                    <p className="break-words mt-0.5">{job.error_message}</p>
                  </div>
                )}

                {errors.length === 0 ? (
                  <p className="text-sm text-gray-500">
                    No per-row errors recorded.
                  </p>
                ) : (
                  <>
                    <p className="text-xs text-gray-500">
                      First {errors.length} validation issues. Download the full
                      report for the complete list.
                    </p>
                    <div className="border border-gray-200 rounded-md overflow-hidden">
                      <table className="min-w-full text-xs">
                        <thead className="bg-gray-50 text-gray-500 uppercase tracking-wide">
                          <tr>
                            <th className="px-3 py-2 text-left w-16">Line</th>
                            <th className="px-3 py-2 text-left">Reason</th>
                          </tr>
                        </thead>
                        <tbody>
                          {errors.map((e, i) => (
                            <tr key={i} className="border-t border-gray-100">
                              <td className="px-3 py-1.5 font-mono text-gray-600">{e.line}</td>
                              <td className="px-3 py-1.5 text-rose-700 break-words">{e.error}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </>
                )}
              </>
            )}
          </div>

          <div className="px-5 py-3 border-t border-gray-200 bg-gray-50 flex justify-between gap-2">
            {job && (job.failed_rows || 0) > 0 ? (
              <a
                href={errorsCsvUrl(jobId)}
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center gap-1.5 px-3 py-2 text-sm border border-gray-200 rounded-md hover:bg-gray-50"
              >
                <Download size={14} /> Download CSV report
              </a>
            ) : <span />}
            <button onClick={onClose} className="px-3 py-2 text-sm text-gray-700 hover:bg-gray-100 rounded-md">
              Close
            </button>
          </div>
        </div>
      </div>
    </>
  );
};

export default ImportsPage;
